import * as d3 from "d3";

import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import {
  LayoutDimension,
  PositionType
} from "src/organization/pages/project/environment/metrics/http/types";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

export const move = (x: number, y: number) => {
  return `translate(${x}, ${y})`;
};

export const getLayoutPositions = (dms: LayoutDimension): PositionType => ({
  xRangeStart: dms.left,
  xRangeEnd: (dms?.width || 0) - dms.right,
  yRangeEnd: dms.bottom,
  yRangeStart: dms.height - dms.bottom,
  xTransform: move(0, dms.height - dms.bottom),
  yTransform: move(dms.left, dms.top),
  width: dms.width || 400,
  height: dms.height
});

type BandAxisGeneratorProps = {
  xDomain: Array<any>;
  yDomain: Array<any>;
  dimension: LayoutDimension;
};

export const getBandAxis = (props: BandAxisGeneratorProps) => {
  const { xDomain, yDomain, dimension } = props;

  const positions = getLayoutPositions(dimension);

  const xScale = d3
    .scaleBand<Date>()
    .domain(xDomain)
    .range([positions.xRangeStart, positions.xRangeEnd])
    .padding(0.171);

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([positions.yRangeStart, positions.yRangeEnd]);

  return {
    yScale,
    xScale,
    positions
  };
};

export const generateLabelProgression = (
  progressionCount: number,
  maxPoint: number = 0
) => {
  const totalDistance = maxPoint;
  const commonDifference = totalDistance / (progressionCount - 1);
  const generatedProgression: number[] = [];
  let currentValue = 0;

  for (let i = 0; i < progressionCount; i++) {
    generatedProgression.push(currentValue);
    currentValue += commonDifference;
  }

  return generatedProgression;
};

/**
 * Formats a number with optional abbreviation.
 *
 * @param value - The number to format.
 * @param precision - The number of decimal places to include.
 * @param {boolean} abbreviateK - Whether to abbreviate thousands with 'k'.
 * @returns The formatted number as a string.
 */
export const formatNumber = ({
  value = 0,
  precision = 0,
  abbreviateK = true
}: {
  value?: number;
  precision?: number;
  abbreviateK?: boolean;
}) => {
  if (abbreviateK && value >= 1000) {
    return `${(value / 1000).toFixed(precision)}k`;
  }
  return precision !== undefined ? value.toFixed(precision) : value.toString();
};

export const getDefaultDomain = (
  timeframe?: TimeFrameType,
  defaultYMax = 5
) => {
  const range = timeframeToRange(timeframe);
  return { yDomain: [0, defaultYMax], xDomain: [range.start, range.end] };
};
